import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, MessageSquareText,
  ChevronLeft, ChevronRight, Activity, BookOpen, LogOut
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../components/ui/tooltip";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/projects", icon: FolderKanban, label: "Projects" },
  { to: "/diary", icon: BookOpen, label: "Engineering Diary" },
  { to: "/chat", icon: MessageSquareText, label: "AI Assistant" },
];

export default function Sidebar({ collapsed, onToggle, onLogout }) {
  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-screen bg-[#003057] text-white flex flex-col z-40 sidebar-transition ${
          collapsed ? "w-16" : "w-[260px]"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-white/10">
          {collapsed ? (
            <span className="text-lg font-extrabold tracking-tighter text-white">Tf</span>
          ) : (
            <div className="flex flex-col">
              <span className="text-[22px] font-extrabold tracking-tight leading-none text-white">
                Testflow
              </span>
              <span className="text-[8px] font-semibold tracking-[0.25em] uppercase text-white/50 mt-0.5">
                POWERED BY FLOWINGO
              </span>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1" data-testid="sidebar-nav">
          {navItems.map((item) => (
            <Tooltip key={item.to}>
              <TooltipTrigger asChild>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-[#266497] text-white"
                        : "text-white/70 hover:text-white hover:bg-white/10"
                    } ${collapsed ? "justify-center" : ""}`
                  }
                >
                  <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right" className="bg-[#003057] text-white border-white/20">
                  {item.label}
                </TooltipContent>
              )}
            </Tooltip>
          ))}
        </nav>

        {/* User & Controls */}
        <div className="p-2 border-t border-white/10 space-y-1">
          {onLogout && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn"
              onClick={onLogout}
              className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start"
            >
              <LogOut className="w-4 h-4" />
              {!collapsed && <span className="ml-2 text-xs">Sign Out</span>}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            data-testid="sidebar-toggle"
            onClick={onToggle}
            className="w-full text-white/70 hover:text-white hover:bg-white/10"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            {!collapsed && <span className="ml-2 text-xs">Collapse</span>}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
